/**
 * Gate Deviation Dialog Component
 *
 * Displays a scatter plot showing gate deviations for selected operations.
 * Features:
 * - Color-coded by operation type (arrival/departure/etc)
 * - Different markers for same/opposite direction
 * - Clickable tooltips that can be pinned to the chart
 * - Unit conversion support
 * - Interactive hover and click tooltips
 */

import React, {
  FC,
  Dispatch,
  SetStateAction,
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, LineElement } from 'chart.js';

import { DialogBody, DialogFooter, Button, Size } from '@blueprintjs/core-modern';
import { Dialog } from 'src/componentsModern';
import { useDataSelectors } from 'src/@operations/reducers';
import { OperationSummary } from 'src/app/gql/__generated__/hooks';
import { useConfigSelectors, useLanguageSelectors } from 'src/app/reducers';
import { getOperationTheme, getDistance } from 'src/utils';
import {
  INPUT_RETURN_TYPES,
  UNIT_METER,
  UNIT_KILOMETER,
  UNIT_NAUTICAL_MILE,
  UNIT_MILE,
  UNIT_FOOT,
} from 'src/constants';

import { DeviationTooltip as DeviationTooltipType, TGateValues } from './types';
import { DeviationTooltip, DeviationChartLegend } from './components';
import {
  convertGateIntersectionData,
  getOperationColor,
  isOppositeDirection,
  calculateYAxisConfig,
  calculateXAxisConfig,
  exportChartAsImage,
  getTooltipRenderData,
  getGateXCoordinates,
  convertGateVerticalDistance,
} from './helpers';
import { CHART_CONSTANTS, MARKER_STYLES } from './constants';
import { TVerticalDistanceUnit, TDistanceUnit } from 'src/utils/interfaces';
import { GraphLoading } from '../TrackProfile/TrackProfileDialog.styles';
import { gateBackgroundPlugin } from './chartPlugins';
import { meterToFoot } from '@ems/client-design-system';

ChartJS.register(LinearScale, PointElement, LineElement, gateBackgroundPlugin);

export interface IGateDeviationGraph {
  selectedOperationIds: number[];
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export const GateDeviationDialog: FC<IGateDeviationGraph> = ({
  selectedOperationIds,
  isOpen,
  setIsOpen,
}) => {
  // Get configuration and theme
  const configSelectors = useConfigSelectors();
  const units = configSelectors.getUnits();
  const selectedTrackTheme = configSelectors.getTheme('operations');
  const colorList = getOperationTheme(selectedTrackTheme);

  // Get translations
  const languageSelectors = useLanguageSelectors();
  const {
    screens: {
      operations: { deviationGraph },
    },
    components: {
      dialogs: { closeDialog, gateDeviationHeader },
      ruleDetails: {
        fields: {
          gateWidth: gateWidthLabel,
          floorHeight: floorHeightLabel,
          ceilingHeight: ceilingHeightLabel,
          gateValues: gateValuesLabel,
        },
      },
    },
    fields: {
      operations: {
        gateInterceptsCountOf,
        gateDirectionShort,
        gateAltitudeShort,
        gateDirection,
        gateDirectionSame,
        gateDirectionOpposite,
      },
    },
  } = languageSelectors.getLanguage();

  // Get selected operations data
  const dataSelectors = useDataSelectors();
  const { data } = dataSelectors.getDataInformation();
  const operationsMap = data as Map<number, OperationSummary>;
  const selectedOperations: OperationSummary[] = selectedOperationIds
    .map(id => operationsMap?.get(id))
    .filter((operation): operation is OperationSummary => Boolean(operation));
  const gateName = selectedOperations[0]?.gateIntersections?.[0]?.gate?.name || '';

  // State for pinned tooltips (user can click to pin/unpin)
  const [clickedTooltips, setClickedTooltips] = useState<DeviationTooltipType[]>([]);
  // State for temporary tooltip (shown on hover)
  const [hoverTooltip, setHoverTooltip] = useState<DeviationTooltipType | null>(null);
  // State for loading buffer
  const [isLoadBuffer, setIsLoadBuffer] = useState(true);
  // State for gate dimensions values
  const [gateValues, setGateValues] = useState<TGateValues | null>(null);

  // Chart management
  const chartRef = useRef<any>(null); // Reference to Chart.js instance
  const chartContainerRef = useRef<HTMLDivElement>(null); // Reference for export functionality
  // Track current hover state to prevent unnecessary updates
  const currentHoverRef = useRef<{ datasetIndex: number; dataIndex: number } | null>(null);

  const gate = useMemo(() => {
    if (selectedOperations.length === 0) {
      return null;
    }
    return selectedOperations[0]?.gateIntersections?.[0]?.gate;
  }, [selectedOperations]);

  const conversionSettings = useMemo(() => {
    // Calculate gate width in m to determine if we should override the unit
    const gateWidthInMeters = gate ? getGateXCoordinates(gate, 'meters') : null;
    const gateWidthInFeet: number = meterToFoot(gateWidthInMeters);

    // If unit is m or km AND gate width is less than 1000 feet, use meters
    let convertTo = units.distance;

    // Design system conversion has a hardcoded switch if values are less than 10000 ft/m
    if (
      (units.distance === UNIT_METER || units.distance === UNIT_KILOMETER) &&
      gateWidthInMeters < 10000
    ) {
      convertTo = UNIT_METER as TDistanceUnit;
    }
    if (
      (units.distance === UNIT_FOOT ||
        units.distance === UNIT_MILE ||
        units.distance === UNIT_NAUTICAL_MILE) &&
      gateWidthInFeet < 10000
    ) {
      convertTo = UNIT_FOOT as TDistanceUnit;
    }

    return {
      convertTo,
      convertFrom: UNIT_METER as TDistanceUnit,
      returnValueType: INPUT_RETURN_TYPES.NUMBER,
    };
  }, [units.distance, gate]);

  const conversionSettingsVertical = useMemo(
    () => ({
      convertTo: units.distanceVertical,
      convertFrom: UNIT_FOOT as TVerticalDistanceUnit,
      returnValueType: INPUT_RETURN_TYPES.NUMBER,
    }),
    [units.distanceVertical]
  );

  // Reset hover state when dialog closes or operations change
  useEffect(() => {
    if (!isOpen) {
      currentHoverRef.current = null;
      setHoverTooltip(null);
    }
  }, [isOpen]);

  // Trigger chart resize when dialog opens (Chart.js handles this automatically)
  useEffect(() => {
    if (isOpen && chartRef.current) {
      // Small delay to allow dialog animation to complete
      const timer = setTimeout(() => {
        chartRef.current?.resize();
      }, CHART_CONSTANTS.CHART_RESIZE_DELAY);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Create the gate values when chart loads
  useEffect(() => {
    if (isOpen) {
      const gateWidth = getDistance(
        getGateXCoordinates(gate, 'meters'),
        conversionSettings
      ) as number;
      // Gate floor/ceiling altitude are in feet,
      setGateValues({
        ceilingAltitude: convertGateVerticalDistance(
          gate?.geometry?.ceilingAltitude,
          conversionSettingsVertical
        ),
        floorAltitude: convertGateVerticalDistance(
          gate?.geometry?.floorAltitude,
          conversionSettingsVertical
        ),
        gateWidth,
        gateLeft: -gateWidth / 2,
        gateRight: gateWidth / 2,
      });
    }
  }, [isOpen, conversionSettings, conversionSettingsVertical, gate]);

  /**
   * Exports the chart (including pinned tooltips) as a JPG image
   */
  const handleExportChart = async () => {
    if (chartContainerRef.current) {
      try {
        await exportChartAsImage(chartContainerRef.current);
      } catch (error) {
        console.error('Failed to export chart:', error);
      }
    }
  };

  /**
   * Generate chart datasets from selected operations
   * Each operation becomes a dataset with one or more data points (one per gate intersection)
   */
  const chartData = useMemo(() => {
    const datasets = selectedOperations
      .map(operation => {
        const dataPoints = convertGateIntersectionData(
          operation,
          conversionSettings,
          conversionSettingsVertical
        );
        if (dataPoints.length === 0) {
          return null;
        }

        const color = getOperationColor(operation.operationType, colorList);
        const isOpposite = isOppositeDirection(operation);
        return {
          label: operation.acid,
          data: dataPoints,
          backgroundColor: color,
          pointRadius: CHART_CONSTANTS.POINT_RADIUS,
          pointHoverRadius: CHART_CONSTANTS.POINT_RADIUS, // Prevent size change on hover
          pointHoverBorderWidth: 0, // Prevent border width change on hover
          pointStyle: isOpposite ? MARKER_STYLES.OPPOSITE_DIRECTION : MARKER_STYLES.SAME_DIRECTION,
        };
      })
      .filter(Boolean);

    return { datasets };
  }, [selectedOperations, colorList, conversionSettings, conversionSettingsVertical]);

  // Memoize hover handler to prevent infinite re-render loops
  // Only update state if the hovered element actually changed
  const handleHover = useCallback((event: any, elements: any[], chart: any) => {
    // Only process if we have elements (direct point intersection)
    if (elements.length === 0) {
      if (currentHoverRef.current !== null) {
        currentHoverRef.current = null;
        setHoverTooltip(null);
      }
      return;
    }

    const element = elements[0];
    const datasetIndex = element.datasetIndex;
    const dataIndex = element.index;

    // Only update if the hovered element actually changed
    if (
      currentHoverRef.current?.datasetIndex !== datasetIndex ||
      currentHoverRef.current?.dataIndex !== dataIndex
    ) {
      const chartElement = chart.getDatasetMeta(datasetIndex).data[dataIndex];
      currentHoverRef.current = { datasetIndex, dataIndex };
      setHoverTooltip({
        datasetIndex,
        dataIndex,
        x: chartElement.x,
        y: chartElement.y,
      });
    }
  }, []);

  // Memoize click handler to prevent unnecessary re-renders
  const handleClick = useCallback((event: any, elements: any[], chart: any) => {
    if (elements.length > 0) {
      const element = elements[0];
      const chartElement = chart.getDatasetMeta(element.datasetIndex).data[element.index];

      // Use functional update to avoid needing clickedTooltips in dependencies
      setClickedTooltips(prev => {
        // Check if this tooltip is already pinned
        const existingIndex = prev.findIndex(
          t => t.datasetIndex === element.datasetIndex && t.dataIndex === element.index
        );

        // Only add if not already pinned (removal is handled by DeviationTooltip component)
        if (existingIndex === -1) {
          return [
            ...prev,
            {
              datasetIndex: element.datasetIndex,
              dataIndex: element.index,
              x: chartElement.x,
              y: chartElement.y,
            },
          ];
        }
        return prev;
      });
    }
  }, []);

  /**
   * Configure chart options including axis ranges and interactions
   * Axis ranges are calculated dynamically based on data with a 20% buffer
   */
  const options = useMemo(() => {
    const isFeet = units.distanceVertical === 'ft';

    // Calculate axis configurations from data
    const yAxisConfig = calculateYAxisConfig(gateValues, isFeet);
    const xAxisConfig = calculateXAxisConfig(gateValues, isFeet, conversionSettings);

    return {
      responsive: true,
      maintainAspectRatio: true,
      animation: false as const,
      // Handle hover to show temporary tooltip
      onHover: handleHover,
      // Handle click to pin/unpin tooltips
      onClick: handleClick,
      interaction: {
        intersect: true, // Only trigger on direct intersection, not just proximity
        mode: 'point' as const, // Only interact with points, not the entire dataset
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
          intersect: true, // Only trigger on direct intersection
        },
        // Pass gate dimensions to the plugin
        gateBackground: {
          // Chart plugins strips the object, so we need to stringify it
          gateId: gate?.id,
          gateValues: gateValues ? JSON.stringify(gateValues) : null,
          conversionSettingsVertical: JSON.stringify(conversionSettingsVertical),
          conversionSettings: JSON.stringify(conversionSettings),
        },
      },
      scales: {
        // X-axis: Lateral deviation (centered at 0)
        x: {
          min: xAxisConfig.min,
          max: xAxisConfig.max,
          ticks: {
            stepSize: xAxisConfig.stepSize,
            // Force Chart.js to use our exact tick values for miles
            ...(xAxisConfig.ticks && {
              callback(value: any) {
                // Only show ticks that are in our explicit tick array
                return xAxisConfig.ticks!.includes(Number(value.toFixed(2))) ? value : '';
              },
            }),
          },
          // Force explicit tick generation for miles
          ...(xAxisConfig.ticks && {
            afterBuildTicks(axis: any) {
              axis.ticks = xAxisConfig.ticks!.map(value => ({ value }));
            },
          }),
          title: {
            display: true,
            text: `${deviationGraph.xAxisLabel.replace('{unit}', conversionSettings.convertTo)}`,
          },
          grid: {
            // Emphasize the center line (0 deviation)
            color: (context: any) =>
              context.tick.value === 0
                ? CHART_CONSTANTS.CENTER_LINE_COLOR
                : CHART_CONSTANTS.GRID_LINE_COLOR,
            lineWidth: (context: any) =>
              context.tick.value === 0
                ? CHART_CONSTANTS.CENTER_LINE_WIDTH
                : CHART_CONSTANTS.GRID_LINE_WIDTH,
          },
        },
        // Y-axis: Altitude
        y: {
          min: yAxisConfig.min,
          max: yAxisConfig.max,
          ticks: {
            stepSize: yAxisConfig.stepSize,
            // Force Chart.js to use our exact tick values
            ...(yAxisConfig.ticks && {
              callback(value: any) {
                // Only show ticks that are in our explicit tick array
                return yAxisConfig.ticks!.includes(Number(value.toFixed(2))) ? value : '';
              },
            }),
          },
          // Force explicit tick generation
          ...(yAxisConfig.ticks && {
            afterBuildTicks(axis: any) {
              axis.ticks = yAxisConfig.ticks!.map(value => ({ value }));
            },
          }),
          title: {
            display: true,
            text: `${deviationGraph.yAxisLabel.replace(
              '{unit}',
              conversionSettingsVertical.convertTo
            )}`,
          },
        },
      },
    };
  }, [
    selectedOperations,
    units,
    conversionSettings,
    handleHover,
    handleClick,
    deviationGraph,
    gateValues,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadBuffer(false);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Memoize hover tooltip render data to prevent unnecessary recalculations
  const hoverTooltipRenderData = useMemo(() => {
    if (!hoverTooltip) {
      return null;
    }
    return getTooltipRenderData(hoverTooltip, chartData);
  }, [hoverTooltip?.datasetIndex, hoverTooltip?.dataIndex, chartData]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      isCloseButtonShown
      size={Size.LARGE}
      title={`${gateDeviationHeader} ${gateName ? `- ${gateName}` : ''}`}>
      <DialogBody>
        <GraphLoading loading={isLoadBuffer} size="l">
          <div ref={chartContainerRef}>
            <div
              style={{
                width: '100%',
                height: '100%',
                minHeight: `${CHART_CONSTANTS.MIN_CHART_HEIGHT}px`,
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1rem',
              }}>
              <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                <Scatter ref={chartRef} data={chartData} options={options} />

                {hoverTooltip && hoverTooltipRenderData && (
                  <DeviationTooltip
                    key={`${hoverTooltip.datasetIndex}-${hoverTooltip.dataIndex}`}
                    tooltip={hoverTooltip}
                    dataPoint={hoverTooltipRenderData.dataPoint}
                    label={hoverTooltipRenderData.label}
                    distanceUnit={units.distanceVertical}
                    intersectionIndex={hoverTooltipRenderData.intersectionIndex}
                    totalIntersections={hoverTooltipRenderData.totalIntersections}
                    translations={{
                      gateInterceptsCountOf,
                      gateDirectionShort,
                      gateAltitudeShort,
                    }}
                    onRemove={() => setHoverTooltip(null)}
                  />
                )}
                {clickedTooltips.map((tooltip, index) => {
                  const renderData = getTooltipRenderData(tooltip, chartData);
                  if (!renderData) {
                    return null;
                  }

                  return (
                    <DeviationTooltip
                      key={`clicked-${tooltip.datasetIndex}-${tooltip.dataIndex}`}
                      tooltip={tooltip}
                      dataPoint={renderData.dataPoint}
                      label={renderData.label}
                      distanceUnit={units.distanceVertical}
                      intersectionIndex={renderData.intersectionIndex}
                      totalIntersections={renderData.totalIntersections}
                      translations={{
                        gateInterceptsCountOf,
                        gateDirectionShort,
                        gateAltitudeShort,
                      }}
                      onRemove={() =>
                        setClickedTooltips(clickedTooltips.filter((_, i) => i !== index))
                      }
                    />
                  );
                })}
              </div>
            </div>
            <DeviationChartLegend
              translations={{
                gateDirection,
                gateDirectionSame,
                gateDirectionOpposite,
                gateWidth: gateWidthLabel,
                floorHeight: floorHeightLabel,
                ceilingHeight: ceilingHeightLabel,
                gateValues: gateValuesLabel,
              }}
              gateValues={gateValues}
              units={{
                vertical: conversionSettingsVertical.convertTo,
                horizontal: conversionSettings.convertTo,
              }}
            />
          </div>
        </GraphLoading>
      </DialogBody>
      <DialogFooter>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            text={deviationGraph.clearTags}
            disabled={clickedTooltips.length === 0}
            onClick={() => setClickedTooltips([])}
          />
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              text={deviationGraph.exportButton}
              onClick={handleExportChart}
              disabled={isLoadBuffer}
            />
            <Button
              intent="primary"
              text={closeDialog}
              onClick={() => {
                setIsOpen(false);
              }}
            />
          </div>
        </div>
      </DialogFooter>
    </Dialog>
  );
};
